import { useCallback, useEffect, useRef } from 'react';

import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

const DEFAULT_POPUP_CLOSED_POLL_INTERVAL_MS = 1000;

export default function usePopupSession() {
  const { toasterStore } = useStores();
  const popupIntervalRef = useRef<number | null>(null);

  // Stop watching the popup when the page unmounts so a popup closed after
  // navigation does not fire the onClosed callback into an unmounted tree.
  useEffect(() => {
    return () => {
      if (popupIntervalRef.current) {
        window.clearInterval(popupIntervalRef.current);
      }
    };
  }, []);

  // Browsers only allow window.open() to spawn a popup while the click's user
  // activation is still live; callers open a blank popup synchronously in the
  // click handler, then redirect it once an async request resolves.
  const openBlankPopup = useCallback(
    (failureMessage: string) => {
      const popup = window.open('', '_blank', 'popup,width=600,height=800');
      if (!popup) {
        toasterStore.push(alert(failureMessage, AlertTypes.error));

        return null;
      }

      // Sever the opener reference so the third-party page cannot navigate this
      // tab (reverse tabnabbing); `noopener` is not an option because it makes
      // window.open return null, and callers need the handle to redirect/watch it.
      popup.opener = null;

      return popup;
    },
    [toasterStore],
  );

  // A previous popup may still be polling (e.g. the action was triggered twice);
  // stop the old watcher so a single close cannot fire onClosed twice.
  const watchPopupClose = useCallback(
    (
      popup: Window,
      onClosed: () => void,
      intervalMs: number = DEFAULT_POPUP_CLOSED_POLL_INTERVAL_MS,
    ) => {
      if (popupIntervalRef.current) {
        window.clearInterval(popupIntervalRef.current);
        popupIntervalRef.current = null;
      }

      popupIntervalRef.current = window.setInterval(() => {
        if (!popup.closed) {
          return;
        }

        if (popupIntervalRef.current) {
          window.clearInterval(popupIntervalRef.current);
          popupIntervalRef.current = null;
        }

        onClosed();
      }, intervalMs);
    },
    [],
  );

  return { openBlankPopup, watchPopupClose };
}
